import React, { KeyboardEvent, useEffect, useMemo } from 'react';

import styled from 'styled-components';

import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UIButton, { EUIButtonVariant } from '@aquariuxfintech/aqx-trader-react-ui/button';
import UIFormItem, { EUIFormItemVariant } from '@aquariuxfintech/aqx-trader-react-ui/form';
import { ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIInput, { EUIInputVariant } from '@aquariuxfintech/aqx-trader-react-ui/input';
import UITab, { EUITabVariant } from '@aquariuxfintech/aqx-trader-react-ui/tab';
import UIText from '@aquariuxfintech/aqx-trader-react-ui/text';

import { ELoginAs } from '@aqx-core/common/api/auth';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { useLoginAsTabs } from '@aqx-core/common/hooks/auth/use-login-as-tab';
import { useAuthLoading, usePrefilledLoginDetails } from '@aqx-core/common/services/auth';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { useAppTheme } from '@aqx-core/common/theme';
import { getEnterKeycode } from '@aqx-core/common/utils/keyboard';
import { useLoginForm } from '@aqx-member/web/hooks';
import { useExperienceAccountRedirect } from '@aqx-member/web/hooks/experience/useExperienceAccountRedirect';

import LoginForgotPassword from './forgot-password';
import LoginSignUpAction from './sign-up';

const LoginForm: FCC = () => {
  useExperienceAccountRedirect();
  const { t } = useTranslation();
  const { theme } = useAppTheme();

  const { tabs, selectedTab, onTabChange } = useLoginAsTabs();
  const prefilledDetails = usePrefilledLoginDetails();
  const { userId: prefilledUserId, password: prefilledPassword } = prefilledDetails || {};

  const { useFormMethods, submit } = useLoginForm({ selectedTab });

  const authLoginLoading = useAuthLoading();

  const { watch, formState, register, reset, setValue } = useFormMethods;

  const [userId, password] = watch(['userId', 'password']);

  const {
    errors: { userId: userIdError, password: passwordError }
  } = formState;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (getEnterKeycode(event)) {
      event.preventDefault();
      submit();
    }
  };

  const handleTabChange = (nextValue: ELoginAs) => {
    // reset the form
    reset();
    onTabChange(nextValue);
  };

  const { userIdInputLabel, userIdInputPlaceholder } = useMemo(() => {
    const isLiveTab = selectedTab === ELoginAs.LIVE;
    return {
      userIdInputLabel: isLiveTab ? `${t('Email')} / ${t('Username')}` : t('Account ID'),
      userIdInputPlaceholder: isLiveTab
        ? `${t('Enter your email')} / ${t('Username').toLowerCase()}`
        : t('Enter your account ID')
    };
  }, [selectedTab, t]);

  const isLoginBtnDisabled = useMemo(() => {
    return !userId || !password;
  }, [userId, password]);

  useEffect(() => {
    if (prefilledUserId) {
      setValue('userId', prefilledUserId);
      setValue('password', prefilledPassword ?? '');
      authenticationViewController.resetPrefilledValues();
    }
  }, [prefilledUserId, prefilledPassword, setValue]);

  return (
    <>
      <FormProvider {...useFormMethods}>
        <StyledContainer onSubmit={submit}>
          <UIText typography={ETypography.DISPLAY_M_BOLD}>{t('Log in')}</UIText>
          <UITab
            variant={EUITabVariant.UNDERLINED}
            tabList={tabs}
            selectedValue={selectedTab}
            onClick={handleTabChange}
            backgroundColor={theme.themeColors.background.default}
            stretch
            dataTestId={EDataTestId.LOGIN_ACCOUNT_TYPE}
            disabled={authLoginLoading}
          />

          <StyledFormInputsContainer>
            <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={userIdInputLabel} size="large">
              <UIInput
                {...register('userId')}
                variant={EUIInputVariant.BORDERED_TEXT}
                placeholder={userIdInputPlaceholder}
                value={userId}
                error={userIdError?.message}
                onKeyDown={handleKeyDown}
                dataTestId={EDataTestId.LOGIN_USER_ID}
                size="large"
              />
            </UIFormItem>

            <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={t('Password')} size="large">
              <UIInput
                {...register('password')}
                variant={EUIInputVariant.BORDERED_PASSWORD}
                placeholder={t('Enter your password')}
                value={password}
                error={passwordError?.message}
                defaultShowPassword={false}
                onKeyDown={handleKeyDown}
                dataTestId={EDataTestId.LOGIN_PASSWORD}
                size="large"
              />

              <LoginForgotPassword selectedTab={selectedTab} />
            </UIFormItem>
          </StyledFormInputsContainer>
          <StyledButtonContainer>
            <UIButton
              variant={EUIButtonVariant.FILLED_THEMED}
              stretch={true}
              loading={authLoginLoading}
              dataTestId={EDataTestId.LOGIN_SUBMIT}
              size="large"
              disabled={isLoginBtnDisabled}
            >
              {t('Sign In')}
            </UIButton>
          </StyledButtonContainer>
        </StyledContainer>
      </FormProvider>
      <LoginSignUpAction selectedTab={selectedTab} />
    </>
  );
};

export default LoginForm;

const StyledContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.XXXL}px;
  width: 512px;
`;

const StyledButtonContainer = styled.div`
  display: flex;
`;

const StyledFormInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.XL}px;
`;
